import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9734bttfb6ioa7s1ufg';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 4000);
      return;
    }
    setStatus('submitting');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const code = (parsed as { code?: string })?.code;
      const serverMsg =
        (parsed as { meta?: { message?: string } })?.meta?.message ||
        (parsed as { message?: string })?.message ||
        '';
      if (response.ok && code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-primary-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          <div>
            <h3 className="text-xl font-heading text-background-50 mb-4">Kakobuy Finds</h3>
            <p className="text-sm text-background-50/50 leading-relaxed">
              Your ultimate resource hub for Kakobuy spreadsheet product discoveries, shopping guides, and curated fashion finds updated regularly.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/40 mb-5">Newsletter</h4>
            <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="">
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full bg-transparent border-b border-background-50/20 text-background-50 text-sm py-2.5 pr-10 outline-none placeholder:text-background-50/30 focus:border-accent-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-background-50/50 hover:text-accent-400 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <i className={`${status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} text-lg`}></i>
                </button>
              </div>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="form-honeypot" />
              {status === 'success' && (
                <p className="text-xs text-accent-400 mt-2">Subscribed! Thanks for joining.</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-red-400 mt-2">{formError || 'Something went wrong.'}</p>
              )}
            </form>
            <p className="text-xs text-background-50/30 mt-3">
              Get weekly spreadsheet updates and new find alerts.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/40 mb-5">Quick Links</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/" className="text-sm text-background-50/60 hover:text-background-50 transition-colors">Home</Link>
              <Link to="/blog/kakobuy-spreadsheet-ultimate-guide-2026" className="text-sm text-background-50/60 hover:text-background-50 transition-colors">Ultimate Guide</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/60 hover:text-background-50 transition-colors">Kakobuy Sheet</a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/40 mb-5">Popular Categories</h4>
            <div className="flex flex-col gap-2.5">
              {categories.slice(0, 5).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-50/60 hover:text-background-50 transition-colors"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-background-50/10 pt-10">
          <div className="text-4xl md:text-6xl lg:text-8xl font-heading text-background-50/8 leading-none tracking-tight select-none">
            KAKOBUY
          </div>
        </div>
      </div>
    </footer>
  );
}